import { useState } from 'react';
import Section from './ui/Section';
import github from '../assets/img/github.svg';

const ENDPOINT = 'https://api.gty.ag/api/v1/email/contact';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FIELDS = [
  { name: 'name', label: 'Name', type: 'text' },
  { name: 'email', label: 'E-mail', type: 'email' },
  { name: 'phone', label: 'Phone', type: 'text' },
];

export function Contact() {
  const [values, setValues] = useState({ name: '', email: '', phone: '', message: '', company: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error

  const update = (e) => setValues((v) => ({ ...v, [e.target.name]: e.target.value }));

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = 'Name is required.';
    if (!values.email.trim()) next.email = 'E-mail is required.';
    else if (!EMAIL_RE.test(values.email)) next.email = 'Please enter a valid e-mail.';
    if (!values.message.trim()) next.message = 'Message is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (values.company) return; // honeypot: silently drop bots
    if (!validate()) return;
    setStatus('submitting');
    try {
      const { company: _hp, ...payload } = values;
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch (err) {
      setStatus('error');
    }
  };

  const buttonText = { idle: 'Send', submitting: 'Sending…', success: 'Thank you', error: 'Try again' }[status];

  return (
    <Section id="contact" heading="Contact us" index="05">
      <div className="flex flex-col items-start justify-between gap-12 md:flex-row md:gap-14">
        <div className="w-full">
          <p className="font-gotham text-xl font-light text-brand-light-gray lg:text-2xl">
            Any questions about your project?
          </p>
          <form className="w-full pt-10 md:max-w-lg md:pt-16" onSubmit={onSubmit} noValidate>
            {/* honeypot */}
            <input type="text" name="company" data-testid="honeypot" value={values.company} onChange={update}
              tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

            {FIELDS.map((f) => (
              <div className="relative mb-10" key={f.name}>
                <input
                  className="peer w-full border-b border-brand-yellow bg-transparent py-2 font-avenir text-xl font-medium text-brand-light-gray caret-white placeholder-transparent focus:outline-none"
                  type={f.type} name={f.name} id={f.name} placeholder=" "
                  value={values[f.name]} onChange={update}
                  aria-invalid={errors[f.name] ? 'true' : undefined}
                  aria-describedby={errors[f.name] ? `${f.name}-error` : undefined}
                />
                <label htmlFor={f.name}
                  className="absolute left-0 top-2 origin-[0] -translate-y-6 scale-75 font-avenir font-medium text-brand-yellow transition-all duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-6 peer-focus:scale-75">
                  {f.label}
                </label>
                {errors[f.name] && <p id={`${f.name}-error`} className="mt-1 font-avenir text-sm text-red-400">{errors[f.name]}</p>}
              </div>
            ))}

            <div className="relative mb-12">
              <textarea
                className="peer w-full border-b border-brand-yellow bg-transparent py-2 font-avenir text-xl font-medium text-brand-light-gray caret-white placeholder-transparent focus:outline-none"
                name="message" id="message" rows="2" placeholder=" "
                value={values.message} onChange={update}
                aria-invalid={errors.message ? 'true' : undefined}
                aria-describedby={errors.message ? 'message-error' : undefined}
              />
              <label htmlFor="message"
                className="absolute left-0 top-2 origin-[0] -translate-y-6 scale-75 font-avenir font-medium text-brand-yellow transition-all duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-6 peer-focus:scale-75">
                Message
              </label>
              {errors.message && <p id="message-error" className="mt-1 font-avenir text-sm text-red-400">{errors.message}</p>}
            </div>

            <button type="submit" disabled={status === 'submitting' || status === 'success'}
              className="flex h-12 w-40 items-center justify-center border-2 border-brand-yellow bg-brand-yellow font-avenir text-lg font-bold uppercase transition-all duration-300 hover:bg-transparent hover:text-brand-light-gray disabled:opacity-70 lg:h-16">
              {buttonText}
            </button>
            {status === 'error' && <p className="mt-3 font-avenir text-sm text-red-400">Something went wrong. Please e-mail info@gty.ag.</p>}
          </form>
        </div>

        <div className="order-first font-gotham md:order-last md:max-w-[250px]">
          <h5 className="pb-6 text-2xl font-medium text-brand-yellow">Contact Info</h5>
          <p className="text-xl text-brand-light-gray">Gravity Technologies AG, Chamerstrasse 172, CH–6300 Zug</p>
          <p className="mt-4 text-xl text-brand-light-gray">Mail: info@gty.ag</p>
          <p className="pb-6 text-xl text-brand-light-gray">Monday – Friday</p>
          <h5 className="pb-6 text-2xl font-medium text-brand-yellow">Follow Us</h5>
          <a href="https://github.com/gravitytech" target="_blank" rel="noreferrer"
            aria-label="Gravity on GitHub" className="inline-block motion-safe:transition motion-safe:hover:-translate-y-1">
            <img src={github} alt="" />
          </a>
        </div>
      </div>
    </Section>
  );
}
